import React from 'react';

export type CardSize = 'small' | 'default' | 'large';

export interface CardProps {
  size?: CardSize;
  interactive?: boolean;
  media?: React.ReactNode;
  preline?: string;
  title?: string;
  titleHref?: string;
  subline?: string;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
}

/**
 * KERN Card Component
 *
 * Usage:
 * <Card title="Card Title" preline="Category" subline="Subtitle">
 *   Card content goes here
 * </Card>
 *
 * <Card interactive title="Interactive Card" titleHref="/link">
 *   Clickable card content
 * </Card>
 */
export function Card({
  size = 'default',
  interactive = false,
  media,
  preline,
  title,
  titleHref,
  subline,
  footer,
  children,
  className = '',
  onClick,
}: CardProps) {
  const classes = [
    'kern-card',
    (interactive || onClick) && 'kern-card--interactive',
    size !== 'default' && `kern-card--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const titleElement =
    title &&
    (titleHref ? (
      <h2 className="kern-title">
        <a href={titleHref} className="kern-link--stretched">
          {title}
        </a>
      </h2>
    ) : (
      <h2 className="kern-title">{title}</h2>
    ));

  return (
    <article
      className={classes}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer' } : undefined}
    >
      {media && <div className="kern-card__media">{media}</div>}
      <div className="kern-card__container">
        {(preline || title || subline) && (
          <header className="kern-card__header">
            <hgroup>
              {preline && <p className="kern-preline">{preline}</p>}
              {titleElement}
              {subline && <p className="kern-subline">{subline}</p>}
            </hgroup>
          </header>
        )}
        {children && (
          <section className="kern-card__body">
            {typeof children === 'string' ? <p className="kern-body">{children}</p> : children}
          </section>
        )}
        {footer && <footer className="kern-card__footer">{footer}</footer>}
      </div>
    </article>
  );
}

export default Card;
